import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { unitExpensesAPI, unitFinancialsAPI, expenseTypesAPI, vehiclesAPI, driversAPI, contractsAPI } from "../lib/api";
import { formatVehicleLabel } from "../lib/formatters";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Truck,
  Calendar,
  Filter,
  Download,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { cn } from "../lib/utils";

export default function ControlFinanciero() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("expenses");
  const [loading, setLoading] = useState(true);
  
  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    unit_id: "all",
    expense_type_id: "all",
    start_date: "",
    end_date: ""
  });
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    unit_id: "",
    expense_type_id: "",
    amount: "",
    currency: "MXN",
    date: new Date().toISOString().split("T")[0],
    contract_id: "",
    driver_id: "",
    description: "",
    receipt_ref: ""
  });
  
  // Financials state
  const [selectedUnit, setSelectedUnit] = useState("");
  const [unitFinancials, setUnitFinancials] = useState(null);
  const [globalFinancials, setGlobalFinancials] = useState(null);
  const [financialPeriod, setFinancialPeriod] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0],
    end_date: new Date().toISOString().split("T")[0]
  });
  
  // Income Distribution Dialog state
  const [incomeDistributionDialog, setIncomeDistributionDialog] = useState(false);
  const [selectedContractForIncome, setSelectedContractForIncome] = useState(null);
  const [incomeDistribution, setIncomeDistribution] = useState(null);
  const [incomeDistributionLoading, setIncomeDistributionLoading] = useState(false);
  const [incomeEditValues, setIncomeEditValues] = useState({});

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === "expenses") {
      fetchExpenses();
    } else if (activeTab === "by-unit" && selectedUnit) {
      fetchUnitFinancials();
    } else if (activeTab === "global") {
      fetchGlobalFinancials();
    }
  }, [activeTab, filters, selectedUnit, financialPeriod]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [expTypesRes, vehiclesRes, driversRes] = await Promise.all([
        expenseTypesAPI.getAll(),
        vehiclesAPI.getAll(),
        driversAPI.getAll()
      ]);
      setExpenseTypes(expTypesRes.data || []);
      setVehicles((vehiclesRes.data || []).filter(v => v.status === "activo"));
      setDrivers((driversRes.data || []).filter(d => d.status === "activo"));
      
      await fetchExpenses();
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      const params = {};
      if (filters.unit_id && filters.unit_id !== "all") params.unit_id = filters.unit_id;
      if (filters.expense_type_id && filters.expense_type_id !== "all") params.expense_type_id = filters.expense_type_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      
      const response = await unitExpensesAPI.getAll(params);
      setExpenses(response.data || []);
    } catch (error) {
      console.error("Error loading expenses:", error);
    }
  };

  const fetchUnitFinancials = async () => {
    if (!selectedUnit) return;
    try {
      const response = await unitFinancialsAPI.getUnitFinancials(selectedUnit, financialPeriod);
      setUnitFinancials(response.data);
    } catch (error) {
      console.error("Error loading unit financials:", error);
      toast.error("Error al cargar finanzas de unidad");
    }
  };

  const fetchGlobalFinancials = async () => {
    try {
      const response = await unitFinancialsAPI.getGlobalFinancials(financialPeriod);
      setGlobalFinancials(response.data);
    } catch (error) {
      console.error("Error loading global financials:", error);
      toast.error("Error al cargar finanzas globales");
    }
  };

  // Income Distribution functions
  const openIncomeDistributionDialog = async (contractId) => {
    setSelectedContractForIncome(contractId);
    setIncomeDistributionLoading(true);
    setIncomeDistributionDialog(true);
    
    try {
      const response = await contractsAPI.getIncomeDistribution(contractId);
      setIncomeDistribution(response.data);
      
      // Initialize edit values from distribution
      const editValues = {};
      response.data.distribution.forEach(item => {
        editValues[item.unit_id] = item.income_amount;
      });
      setIncomeEditValues(editValues);
    } catch (error) {
      console.error("Error loading income distribution:", error);
      toast.error("Error al cargar distribución de ingresos");
      setIncomeDistributionDialog(false);
    } finally {
      setIncomeDistributionLoading(false);
    }
  };

  const handleIncomeValueChange = (unitId, value) => {
    setIncomeEditValues(prev => ({
      ...prev,
      [unitId]: parseFloat(value) || 0
    }));
  };

  const calculateIncomeTotal = () => {
    return Object.values(incomeEditValues).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const saveIncomeDistribution = async () => {
    if (!incomeDistribution) return;
    
    const total = calculateIncomeTotal();
    const contractTotal = incomeDistribution.contract_total;
    
    if (Math.abs(total - contractTotal) > 0.01) {
      toast.error(`La suma ($${total.toLocaleString()}) no coincide con el total del contrato ($${contractTotal.toLocaleString()})`);
      return;
    }
    
    try {
      const items = Object.entries(incomeEditValues).map(([unit_id, income_amount]) => ({
        unit_id,
        income_amount,
        notes: ""
      }));
      
      await contractsAPI.updateIncomeDistribution(selectedContractForIncome, { items });
      toast.success("Distribución de ingresos actualizada");
      setIncomeDistributionDialog(false);
      
      // Refresh unit financials
      if (selectedUnit) {
        fetchUnitFinancials();
      }
    } catch (error) {
      console.error("Error saving income distribution:", error);
      toast.error(error.response?.data?.detail || "Error al guardar distribución");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.expense_type_id || !formData.amount || !formData.date) {
      toast.error("Tipo de gasto, monto y fecha son requeridos");
      return;
    }
    
    try {
      const data = {
        ...formData,
        unit_id: formData.unit_id || null,
        amount: parseFloat(formData.amount),
        contract_id: formData.contract_id || null,
        driver_id: formData.driver_id || null
      };
      
      if (editingExpense) {
        await unitExpensesAPI.update(editingExpense.id, data);
        toast.success("Gasto actualizado");
      } else {
        await unitExpensesAPI.create(data);
        toast.success("Gasto registrado");
      }
      setDialogOpen(false);
      resetForm();
      fetchExpenses();
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al guardar gasto";
      toast.error(msg);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      unit_id: expense.unit_id || "",
      expense_type_id: expense.expense_type_id || "",
      amount: expense.amount?.toString() || "",
      currency: expense.currency || "MXN",
      date: expense.date || "",
      contract_id: expense.contract_id || "",
      driver_id: expense.driver_id || "",
      description: expense.description || "",
      receipt_ref: expense.receipt_ref || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    
    try {
      await unitExpensesAPI.delete(expenseId);
      toast.success("Gasto eliminado");
      fetchExpenses();
    } catch (error) {
      toast.error("Error al eliminar gasto");
    }
  };

  const resetForm = () => {
    setEditingExpense(null);
    setFormData({
      unit_id: "",
      expense_type_id: "",
      amount: "",
      currency: "MXN",
      date: new Date().toISOString().split("T")[0],
      contract_id: "",
      driver_id: "",
      description: "",
      receipt_ref: ""
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const formatMoney = (amount, currency = "MXN") => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: currency
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-MX");
  };

  // Calculate totals for summary
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const unitExpensesTotal = expenses.filter(e => e.unit_id).reduce((sum, e) => sum + (e.amount || 0), 0);
  const generalExpensesTotal = expenses.filter(e => !e.unit_id).reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const isAdmin = user?.role === "admin";

  return (
    <Layout>
      <div className="space-y-6" data-testid="control-financiero-page">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Control Financiero</h1>
            <p className="text-slate-500 mt-1">Ingresos y egresos por unidad</p>
          </div>
          <Button onClick={openNewDialog} className="gap-2" data-testid="new-expense-btn">
            <Plus className="w-4 h-4" />
            Registrar Gasto
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-red-50 to-red-100/50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">Total Egresos</p>
                  <p className="text-2xl font-bold text-red-700">{formatMoney(totalExpenses)}</p>
                </div>
                <div className="w-12 h-12 bg-red-200 rounded-xl flex items-center justify-center">
                  <ArrowDownRight className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Por Unidad</p>
                  <p className="text-2xl font-bold text-blue-700">{formatMoney(unitExpensesTotal)}</p>
                </div>
                <div className="w-12 h-12 bg-blue-200 rounded-xl flex items-center justify-center">
                  <Truck className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100/50 border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Gastos Generales</p>
                  <p className="text-2xl font-bold text-slate-700">{formatMoney(generalExpensesTotal)}</p>
                </div>
                <div className="w-12 h-12 bg-slate-200 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="expenses" className="gap-2">
              <DollarSign className="w-4 h-4" />
              Gastos
            </TabsTrigger>
            <TabsTrigger value="by-unit" className="gap-2">
              <Truck className="w-4 h-4" />
              Por Unidad
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="global" className="gap-2">
                <BarChart3 className="w-4 h-4" />
                Reporte Global
              </TabsTrigger>
            )}
          </TabsList>

          {/* Expenses Tab */}
          <TabsContent value="expenses" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs">Unidad</Label>
                    <Select value={filters.unit_id} onValueChange={(v) => setFilters(prev => ({ ...prev, unit_id: v }))}>
                      <SelectTrigger className="w-48" data-testid="filter-unit">
                        <SelectValue placeholder="Todas las unidades" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="general">Sin unidad (Generales)</SelectItem>
                        {vehicles.map(v => (
                          <SelectItem key={v.id} value={v.id}>{formatVehicleLabel(v)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Tipo de Gasto</Label>
                    <Select value={filters.expense_type_id} onValueChange={(v) => setFilters(prev => ({ ...prev, expense_type_id: v }))}>
                      <SelectTrigger className="w-48" data-testid="filter-expense-type">
                        <SelectValue placeholder="Todos los tipos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {expenseTypes.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Desde</Label>
                    <Input
                      type="date"
                      value={filters.start_date}
                      onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                      className="w-40"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Hasta</Label>
                    <Input
                      type="date"
                      value={filters.end_date}
                      onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                      className="w-40"
                    />
                  </div>
                  
                  <Button 
                    variant="outline" 
                    onClick={() => setFilters({ unit_id: "all", expense_type_id: "all", start_date: "", end_date: "" })}
                  >
                    Limpiar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Expenses Table */}
            <Card>
              <CardContent className="p-0">
                {expenses.length === 0 ? (
                  <div className="text-center py-12">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">No hay gastos registrados</p>
                    <Button variant="link" onClick={openNewDialog}>Registrar el primero</Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Unidad</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenses.map((expense) => (
                        <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                          <TableCell>{formatDate(expense.date)}</TableCell>
                          <TableCell>
                            {expense.unit ? (
                              <div className="flex items-center gap-2">
                                <Truck className="w-4 h-4 text-blue-500" />
                                <span>{expense.unit.name}</span>
                              </div>
                            ) : (
                              <Badge variant="outline" className="bg-slate-50">General</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-emerald-100 text-emerald-800">
                              {expense.expense_type?.name || "Sin tipo"}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {expense.description || "-"}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatMoney(expense.amount, expense.currency)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(expense)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              {isAdmin && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDelete(expense.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* By Unit Tab */}
          <TabsContent value="by-unit" className="space-y-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="space-y-1 flex-1 min-w-64">
                    <Label className="text-xs">Seleccionar Unidad</Label>
                    <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                      <SelectTrigger data-testid="select-unit-for-report">
                        <SelectValue placeholder="Seleccionar unidad..." />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicles.map(v => (
                          <SelectItem key={v.id} value={v.id}>{formatVehicleLabel(v)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Desde</Label>
                    <Input
                      type="date"
                      value={financialPeriod.start_date}
                      onChange={(e) => setFinancialPeriod(prev => ({ ...prev, start_date: e.target.value }))}
                      className="w-40"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Hasta</Label>
                    <Input
                      type="date"
                      value={financialPeriod.end_date}
                      onChange={(e) => setFinancialPeriod(prev => ({ ...prev, end_date: e.target.value }))}
                      className="w-40"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {selectedUnit && unitFinancials && (
              <div className="space-y-4">
                {/* Unit Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-green-50 to-green-100/50 border-green-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-green-600">Ingresos</p>
                      <p className="text-2xl font-bold text-green-700">
                        {formatMoney(unitFinancials.summary?.total_income)}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-red-50 to-red-100/50 border-red-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-red-600">Egresos</p>
                      <p className="text-2xl font-bold text-red-700">
                        {formatMoney(unitFinancials.summary?.total_expenses)}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className={cn(
                    "bg-gradient-to-br border",
                    unitFinancials.summary?.utility >= 0 
                      ? "from-emerald-50 to-emerald-100/50 border-emerald-200"
                      : "from-orange-50 to-orange-100/50 border-orange-200"
                  )}>
                    <CardContent className="p-4">
                      <p className={cn("text-sm", unitFinancials.summary?.utility >= 0 ? "text-emerald-600" : "text-orange-600")}>
                        Utilidad
                      </p>
                      <p className={cn("text-2xl font-bold", unitFinancials.summary?.utility >= 0 ? "text-emerald-700" : "text-orange-700")}>
                        {formatMoney(unitFinancials.summary?.utility)}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-blue-600">Margen</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {(unitFinancials.summary?.margin_percent || 0).toFixed(1)}%
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Expense Breakdown */}
                {unitFinancials.expense_breakdown && Object.keys(unitFinancials.expense_breakdown).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Desglose de Gastos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(unitFinancials.expense_breakdown).map(([type, amount]) => (
                          <div key={type} className="flex justify-between items-center py-2 border-b last:border-0">
                            <span className="text-slate-600">{type}</span>
                            <span className="font-medium">{formatMoney(amount)}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Income Details */}
                {unitFinancials.income_details && unitFinancials.income_details.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Detalle de Ingresos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Contrato</TableHead>
                            <TableHead>Cliente</TableHead>
                            <TableHead>Fecha</TableHead>
                            <TableHead className="text-right">Ingreso</TableHead>
                            {isAdmin && <TableHead className="text-center w-20">Acción</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {unitFinancials.income_details.map((income, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{income.contract_folio}</TableCell>
                              <TableCell>{income.client_name}</TableCell>
                              <TableCell>{formatDate(income.date)}</TableCell>
                              <TableCell className="text-right font-medium text-green-600">
                                {formatMoney(income.income)}
                                {income.income_source === "manual" && (
                                  <Badge className="ml-2 text-xs bg-blue-100 text-blue-700">Manual</Badge>
                                )}
                                {income.income_source === "contract_price" && (
                                  <Badge className="ml-2 text-xs bg-green-100 text-green-700">Precio Contrato</Badge>
                                )}
                                {income.income_source === "proportional" && (
                                  <Badge className="ml-2 text-xs bg-amber-100 text-amber-700">Proporcional</Badge>
                                )}
                              </TableCell>
                              {isAdmin && (
                                <TableCell className="text-center">
                                  {income.income_source !== "contract_price" && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openIncomeDistributionDialog(income.contract_id)}
                                      title="Editar distribución de ingresos"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            
            {!selectedUnit && (
              <div className="text-center py-12 bg-slate-50 rounded-lg">
                <Truck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">Selecciona una unidad para ver su reporte financiero</p>
              </div>
            )}
          </TabsContent>

          {/* Global Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="global" className="space-y-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="space-y-1">
                      <Label className="text-xs">Desde</Label>
                      <Input
                        type="date"
                        value={financialPeriod.start_date}
                        onChange={(e) => setFinancialPeriod(prev => ({ ...prev, start_date: e.target.value }))}
                        className="w-40"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs">Hasta</Label>
                      <Input
                        type="date"
                        value={financialPeriod.end_date}
                        onChange={(e) => setFinancialPeriod(prev => ({ ...prev, end_date: e.target.value }))}
                        className="w-40"
                      />
                    </div>
                    
                    <Button onClick={fetchGlobalFinancials}>
                      Actualizar Reporte
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {globalFinancials && (
                <div className="space-y-4">
                  {/* Global Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-green-50 to-green-100/50 border-green-200">
                      <CardContent className="p-4">
                        <p className="text-sm text-green-600">Ingresos Totales</p>
                        <p className="text-2xl font-bold text-green-700">
                          {formatMoney(globalFinancials.global_summary?.total_income)}
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-red-50 to-red-100/50 border-red-200">
                      <CardContent className="p-4">
                        <p className="text-sm text-red-600">Egresos Totales</p>
                        <p className="text-2xl font-bold text-red-700">
                          {formatMoney(globalFinancials.global_summary?.total_expenses)}
                        </p>
                        <p className="text-xs text-red-500 mt-1">
                          (Unidades: {formatMoney(globalFinancials.global_summary?.unit_expenses)} | 
                          Generales: {formatMoney(globalFinancials.global_summary?.general_expenses)})
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className={cn(
                      "bg-gradient-to-br border",
                      globalFinancials.global_summary?.total_utility >= 0 
                        ? "from-emerald-50 to-emerald-100/50 border-emerald-200"
                        : "from-orange-50 to-orange-100/50 border-orange-200"
                    )}>
                      <CardContent className="p-4">
                        <p className={cn("text-sm", globalFinancials.global_summary?.total_utility >= 0 ? "text-emerald-600" : "text-orange-600")}>
                          Utilidad Total
                        </p>
                        <p className={cn("text-2xl font-bold", globalFinancials.global_summary?.total_utility >= 0 ? "text-emerald-700" : "text-orange-700")}>
                          {formatMoney(globalFinancials.global_summary?.total_utility)}
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
                      <CardContent className="p-4">
                        <p className="text-sm text-blue-600">Unidades Activas</p>
                        <p className="text-2xl font-bold text-blue-700">
                          {globalFinancials.units?.length || 0}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Units Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Resumen por Unidad</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Unidad</TableHead>
                            <TableHead className="text-right">Ingresos</TableHead>
                            <TableHead className="text-right">Egresos</TableHead>
                            <TableHead className="text-right">Utilidad</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {globalFinancials.units?.map((unit) => (
                            <TableRow key={unit.unit_id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Truck className="w-4 h-4 text-blue-500" />
                                  <div>
                                    <p className="font-medium">{unit.unit_name}</p>
                                    <p className="text-xs text-slate-500">{unit.unit_plate}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-right text-green-600">
                                {formatMoney(unit.income)}
                              </TableCell>
                              <TableCell className="text-right text-red-600">
                                {formatMoney(unit.expenses)}
                              </TableCell>
                              <TableCell className={cn(
                                "text-right font-medium",
                                unit.utility >= 0 ? "text-emerald-600" : "text-orange-600"
                              )}>
                                {formatMoney(unit.utility)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Dialog for Add/Edit Expense */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingExpense ? "Editar Gasto" : "Registrar Gasto"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unidad (opcional)</Label>
                  <Select value={formData.unit_id || "none"} onValueChange={(v) => setFormData(prev => ({ ...prev, unit_id: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="expense-unit">
                      <SelectValue placeholder="Sin unidad (General)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin unidad (General)</SelectItem>
                      {vehicles.map(v => (
                        <SelectItem key={v.id} value={v.id}>{formatVehicleLabel(v)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Tipo de Gasto *</Label>
                  <Select value={formData.expense_type_id} onValueChange={(v) => setFormData(prev => ({ ...prev, expense_type_id: v }))}>
                    <SelectTrigger data-testid="expense-type">
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseTypes.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monto *</Label>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    required
                    data-testid="expense-amount"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    required
                    data-testid="expense-date"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Chofer (opcional)</Label>
                <Select value={formData.driver_id || "none"} onValueChange={(v) => setFormData(prev => ({ ...prev, driver_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="expense-driver">
                    <SelectValue placeholder="Sin chofer asociado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin chofer</SelectItem>
                    {drivers.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Descripción (opcional)</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detalles del gasto..."
                  data-testid="expense-description"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Referencia/Factura (opcional)</Label>
                <Input
                  value={formData.receipt_ref}
                  onChange={(e) => setFormData(prev => ({ ...prev, receipt_ref: e.target.value }))}
                  placeholder="Número de factura o referencia"
                  data-testid="expense-receipt"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-expense-btn">
                  {editingExpense ? "Actualizar" : "Registrar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Income Distribution Dialog */}
        <Dialog open={incomeDistributionDialog} onOpenChange={setIncomeDistributionDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                Distribución de Ingresos
                {incomeDistribution && (
                  <span className="text-sm font-normal text-slate-500 ml-2">
                    Contrato {incomeDistribution.contract_folio}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {incomeDistributionLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : incomeDistribution && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="bg-slate-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total del Contrato:</span>
                    <span className="font-medium">{formatMoney(incomeDistribution.contract_total)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-600">Suma Asignada:</span>
                    <span className={cn(
                      "font-medium",
                      Math.abs(calculateIncomeTotal() - incomeDistribution.contract_total) < 0.01 
                        ? "text-green-600" 
                        : "text-red-600"
                    )}>
                      {formatMoney(calculateIncomeTotal())}
                    </span>
                  </div>
                  {Math.abs(calculateIncomeTotal() - incomeDistribution.contract_total) >= 0.01 && (
                    <div className="text-xs text-red-500 mt-2">
                      Diferencia: {formatMoney(incomeDistribution.contract_total - calculateIncomeTotal())}
                    </div>
                  )}
                </div>

                {/* Distribution items */}
                <div className="space-y-3">
                  {incomeDistribution.distribution.map((item) => (
                    <div key={item.unit_id} className="flex items-center gap-3 p-3 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.unit_name}</p>
                        <p className="text-xs text-slate-500">{item.unit_plate}</p>
                        {!item.is_editable && (
                          <Badge className="text-xs bg-green-100 text-green-700 mt-1">
                            Precio fijo del contrato
                          </Badge>
                        )}
                      </div>
                      <div className="w-32">
                        <Input
                          type="number"
                          step="any"
                          min="0"
                          value={incomeEditValues[item.unit_id] || 0}
                          onChange={(e) => handleIncomeValueChange(item.unit_id, e.target.value)}
                          disabled={!item.is_editable}
                          className={cn(
                            "text-right",
                            !item.is_editable && "bg-slate-100"
                          )}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {!incomeDistribution.has_vehicle_lines && (
                  <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    Este contrato no tiene precios definidos por unidad. 
                    Puede asignar manualmente el ingreso a cada unidad.
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setIncomeDistributionDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={saveIncomeDistribution}
                disabled={incomeDistribution && Math.abs(calculateIncomeTotal() - incomeDistribution.contract_total) >= 0.01}
              >
                Guardar Distribución
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
